import React, { useRef, useEffect, useState, useImperativeHandle } from "react";
import WaveSurfer from "wavesurfer.js";
import styled from "styled-components";
import Play from "assets/playBlue.svg";
import Pause from 'assets/pause.png'
import { WaveformPlayerHandle, WaveformPlayerProps } from "../types";
import {FormattedMessage} from "react-intl";
import { ProgressSpinner } from "primereact/progressspinner";

// Keep only ONE track playing at a time on the Release Details page.
let currentDetailsPlayingWaveform: {
  trackId: string | number;
  instance: WaveSurfer | null;
} | null = null;

const WaveformPlayer = React.forwardRef<WaveformPlayerHandle, WaveformPlayerProps>(
({ audio, id, active, time = false, onPlayStateChange, onReadyChange }, ref) => {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const currentAudio = audio.find((item) => item.id === String(id));

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!waveformRef.current || !currentAudio?.audio) return;
    setIsReady(false);
    onReadyChange?.(false);

    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#ccc",
      progressColor: "#000000",
      height: 39,
      barWidth: 2,
    });
    wavesurfer.load(currentAudio.audio);
    wavesurferRef.current = wavesurfer;
    wavesurfer.on("ready", () => {
      setDuration(wavesurfer.getDuration());
      setIsReady(true);
      onReadyChange?.(true);
    });
    wavesurfer.on("play", () => {
      // Pause any other playing track on this page.
      if (
        currentDetailsPlayingWaveform &&
        currentDetailsPlayingWaveform.instance &&
        currentDetailsPlayingWaveform.instance !== wavesurfer
      ) {
        try {
          currentDetailsPlayingWaveform.instance.pause();
        } catch {
          // ignore
        }
      }
      currentDetailsPlayingWaveform = { trackId: id, instance: wavesurfer };
      setIsPlaying(true);
      onPlayStateChange?.(true);
    });
    const handlePauseLike = () => {
      if (
        currentDetailsPlayingWaveform &&
        currentDetailsPlayingWaveform.instance === wavesurfer
      ) {
        currentDetailsPlayingWaveform = null;
      }
      setIsPlaying(false);
      onPlayStateChange?.(false);
    };
    wavesurfer.on("pause", handlePauseLike);
    wavesurfer.on("finish", handlePauseLike);
    wavesurfer.on("audioprocess", () => {
      setCurrentTime(wavesurfer.getCurrentTime());
    });

    return () => {
      wavesurfer.destroy();
    };
  }, [id, currentAudio?.audio, onPlayStateChange, onReadyChange]);

  const togglePlay = () => {
    if (isReady) {
      wavesurferRef.current?.playPause();
    }
  };

  useImperativeHandle(ref, () => ({ togglePlay }), [isReady]);

  const stopAccordionToggle = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
  };

  return (
    <Container onMouseDown={stopAccordionToggle} onClick={stopAccordionToggle}>
      {active && (
        <FileDetails>
          <PlayButton onMouseDown={stopAccordionToggle} onClick={(e) => { stopAccordionToggle(e); togglePlay(); }}>
            {!isReady ? (
              <SpinnerWrap>
                <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="6" />
              </SpinnerWrap>
            ) : isPlaying ? (
              <img src={Pause} alt="pause" width={20} height={20} />
            ) : (
              <Play />
            )}
          </PlayButton>
          <TrackInfo>
            <TrackTitle>
              <FormattedMessage
                id="ReleaseDetails.PlatFormWaveformPlayer.TrackTitleMASTER"
                defaultMessage="TrackTitle_MASTER"
              />
            </TrackTitle>
            <TrackFormat>
              <FormattedMessage
                id="ReleaseDetails.PlatFormWaveformPlayer.WAV"
                defaultMessage="WAV"
              />
            </TrackFormat>
          </TrackInfo>
        </FileDetails>
      )}
      <AudioBox style={{ paddingRight: active ? "25px" : "" }}>
        <AudioTime>{time ? formatTime(duration) : formatTime(currentTime)}</AudioTime>
        <Waveform>
          <WaveformContainer ref={waveformRef} onMouseDown={stopAccordionToggle} onClick={stopAccordionToggle} />
        </Waveform>
      </AudioBox>
    </Container>
  );
});

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const WaveformContainer = styled.div`
  max-height: 39px;
  width: 100%;
  cursor: pointer;
  overflow: hidden;
`;

const PlayButton = styled.div`
  cursor:pointer;
`;

const SpinnerWrap = styled.div`
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const FileDetails = styled.div`
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Waveform = styled.div`
  width: 160px;
  height: 39px;
  border-radius: 8px;

  @media (max-width: 768px) {
    width: 120px;
  }
`;

const AudioBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AudioTime = styled.span`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: #00000080;
`;

const TrackInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const TrackTitle = styled.span`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  color: #000000;
`;

const TrackFormat = styled.span`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  color: #00000080;
`;

export default WaveformPlayer;
